import { BarCodeRepository } from "Infrastructure/repositories/api/BarCodeRepository";
import { useKiosk } from "application/contexts/kiosk/KioskContext";
import { useSession } from "application/contexts/session/SessionContext";
import { useUi } from "application/contexts/ui/UIContext";
import { Product } from "domains/catalog/aggregates/Product";
import { ICartContext } from "modules/cart/domain/interfaces/IUseCaseCart";
import { useCartStore } from "modules/cart/infra/store/CartStore";
import { useCallback, useEffect, useRef } from "react";

const barCodeRepository = BarCodeRepository();

export const UseBarCode = (weighing?: boolean) => {
  const refDiv = useRef<HTMLDivElement | null>(null);

  const { kiosk } = useKiosk();
  const { verifySessionIsOpen } = useSession();
  const { toast } = useUi();
  const [addProductCart, setConfigCart] = useCartStore(
    (state: ICartContext) => [state.addProductCart, state.setConfigCart]
  );

  const allowBarcode = useRef(false);

  const codeValue = useRef("");

  useEffect(() => {
    if (kiosk?.enableBarCode) {
      allowBarcode.current = true;
      setConfigCart({
        fullscreen: kiosk?.barCodeMode,
        disableCloseButton: kiosk?.barCodeMode,
        openOnCreate: weighing ? false : true,
        label: "Finalizar",
      });
    } else {
      allowBarcode.current = false;
    }
  }, [kiosk, setConfigCart, weighing]);

  const onSubmit = useCallback(async () => {
    const hasOpenSession = await verifySessionIsOpen();

    if (!hasOpenSession) {
      toast("Falha na conexão com o servidor / caixa fechado", "error");
      codeValue.current = "";
      return;
    }

    if (codeValue.current.length) {
      let product: Product | null = null;

      // Tenta obter o produto diretamente
      product = await barCodeRepository.getProduct(codeValue.current);

      if (!product) {
        // Se não encontrar, tenta quebrar o código
        if (codeValue.current.charAt(0) === "2") {
          const stringWithoutFirstAndLast = codeValue.current.slice(1, -1);
          const sixFirstDigits = stringWithoutFirstAndLast.slice(0, 6);
          const fiveLastDigits = stringWithoutFirstAndLast.slice(-5);

          product = await barCodeRepository.getProduct(sixFirstDigits);

          if (product) {
            const quantidadeFrancionada = Number(fiveLastDigits) / 1000;
            const quantidadeFracionadaFormatada = Number(
              quantidadeFrancionada.toFixed(5)
            );
            addProductCart(
              {
                ...product,
                isWeight: true,
                hideCatalog: true,
                price: (Number(fiveLastDigits) / 1000) * product.price,
              },
              quantidadeFracionadaFormatada,
              [],
              product.adultHood
            );
          }
        }
      } else {
        // Produto encontrado diretamente
        addProductCart(
          { ...product, isBarcodeUnity: true },
          1,
          [],
          product.adultHood
        );
      }

      if (!product) {
        toast("Produto não encontrado", "error");
      }
      codeValue.current = "";
    }
  }, [verifySessionIsOpen, codeValue, addProductCart, toast]);

  const onkeypress = useCallback(
    (key: string) => {
      if (key === "Enter") {
        onSubmit();
      } else {
        codeValue.current = codeValue.current + key;
      }
    },
    [onSubmit]
  );

  useEffect(() => {
    const eventListener = (value: KeyboardEvent) => {
      if (allowBarcode.current)
        onkeypress(value.key);
    };

    window.addEventListener("keypress", eventListener);
    return () => {
      window.removeEventListener("keypress", eventListener);
    };
  }, [onkeypress]);

  const focus = () => {
    refDiv.current?.focus();
  };

  return {
    refDiv,
    kiosk,
    focus,
    alert,
    onkeypress,
  };
};
